import * as THREE from 'three';

import { createFpsSampler } from './fps';

export interface SceneHandle {
  dispose(): void;
}

export interface SceneOptions {
  /**
   * Pose the scene at this elapsed time, render exactly one frame and stop.
   *
   * The animation is integrated from a clock at runtime, which makes any
   * screenshot of it a race. Posing it analytically from a fixed time gives
   * visual regression a frame it can actually compare, and is useful for
   * inspecting one specific moment by hand.
   */
  freezeAtSeconds?: number;
}

/**
 * Placeholder scene. Replace the board/marker meshes with the ported game's
 * terrain and units once the reverse-engineering pass produces real data.
 */
export function createScene(
  canvas: HTMLCanvasElement,
  onFps: (fps: number) => void,
  options: SceneOptions = {},
): SceneHandle {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x0b0d10);

  const camera = new THREE.PerspectiveCamera(55, 1, 0.1, 100);
  camera.position.set(3.2, 2.6, 4.2);
  camera.lookAt(0, 0.4, 0);

  scene.add(new THREE.HemisphereLight(0xbfd4ff, 0x20160c, 1.8));
  const key = new THREE.DirectionalLight(0xffd9a0, 2.4);
  key.position.set(4, 6, 3);
  scene.add(key);

  const board = new THREE.Mesh(
    new THREE.BoxGeometry(3.4, 0.2, 3.4),
    new THREE.MeshStandardMaterial({ color: 0x3a4a3f, roughness: 0.95 }),
  );
  scene.add(board);

  const marker = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.5, 0),
    new THREE.MeshStandardMaterial({ color: 0xc9a227, roughness: 0.35, metalness: 0.4 }),
  );
  marker.position.y = 0.9;
  scene.add(marker);

  // Single definition of the marker's pose at time t. The live loop and the
  // frozen frame both go through it, so a frozen screenshot always depicts a
  // moment the animation actually passes through.
  const poseAt = (seconds: number) => {
    marker.rotation.y = seconds * 0.8;
    marker.position.y = 0.9 + Math.sin(seconds * 1.5) * 0.08;
  };

  const { freezeAtSeconds } = options;
  const frozen = typeof freezeAtSeconds === 'number' && Number.isFinite(freezeAtSeconds);

  const resize = () => {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w === 0 || h === 0) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    // A frozen scene has no loop to pick the new size up on the next frame.
    if (frozen) renderer.render(scene, camera);
  };
  const observer = new ResizeObserver(resize);
  observer.observe(canvas);

  if (frozen) {
    poseAt(freezeAtSeconds);
    resize();
    renderer.render(scene, camera);
  } else {
    resize();

    const timer = new THREE.Timer();
    const fps = createFpsSampler();

    renderer.setAnimationLoop((timestamp) => {
      timer.update(timestamp);
      const dt = timer.getDelta();

      poseAt(timer.getElapsed());
      renderer.render(scene, camera);

      const reading = fps.push(dt);
      if (reading !== null) onFps(reading);
    });
  }

  return {
    dispose() {
      renderer.setAnimationLoop(null);
      observer.disconnect();
      scene.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        object.geometry.dispose();
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        for (const material of materials) material.dispose();
      });
      renderer.dispose();
    },
  };
}
